// Experiencia de memoria para MÓVIL — LIGERA (no funde el celular).
// Decisiones anti-crash:
//   · el layout de fuerzas se calcula UNA sola vez (síncrono), NO hay loop de
//     animación continuo (el parallax por inclinación se quitó: reanimaba cada
//     frame para siempre y fundía el navegador).
//   · SIN filtros de glow (feGaussianBlur es caro en móvil) → halo barato.
//   · nodos acotados para móvil → pan/zoom fluido.
// Conserva: pantalla completa táctil (1 dedo mueve, 2 dedos zoom, tap → bottom
// sheet), toggle de CLUSTERS, y respeto del TEMA (claro/oscuro).

import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Network } from 'lucide-react';
import type { GraphResponse, MemoryNode, Clasif } from '../api';
import { useThemeMode, type ThemeMode } from '@/components/ui/useThemeMode';

const CLASIF: Record<ThemeMode, Record<Clasif, string>> = {
  dark:  { publica: '#34d399', interna: '#60a5fa', restringida: '#fbbf24', confidencial: '#f87171' },
  light: { publica: '#059669', interna: '#2563eb', restringida: '#d97706', confidencial: '#dc2626' },
};
const CLUSTER: Record<ThemeMode, string[]> = {
  dark:  ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
};
const cluster = (mode: ThemeMode, id?: number) =>
  id == null ? (mode === 'dark' ? '#64748b' : '#94a3b8') : CLUSTER[mode][id % CLUSTER[mode].length];

const STAGE: Record<ThemeMode, { bg: string; edge: string; text: string; textDim: string; glass: string; sheet: string; sheetText: string }> = {
  dark: {
    bg: 'radial-gradient(120% 90% at 50% 30%, #141d31 0%, #0b1220 55%, #050810 100%)',
    edge: '#334155', text: '#cbd5e1', textDim: '#94a3b8',
    glass: 'bg-white/10 border-white/15 text-slate-100', sheet: 'bg-slate-900 border-white/10', sheetText: '#e2e8f0',
  },
  light: {
    bg: 'radial-gradient(120% 90% at 50% 28%, #ffffff 0%, #eef2f7 55%, #e2e8f0 100%)',
    edge: '#cbd5e1', text: '#334155', textDim: '#64748b',
    glass: 'bg-black/5 border-black/10 text-slate-700', sheet: 'bg-white border-black/10', sheetText: '#0f172a',
  },
};

// Tope para MÓVIL (más chico que desktop): pan/zoom fluido en teléfonos.
const MOBILE_MAX = 70;

interface P { x: number; y: number }

// Acota a las memorias más reforzadas + su procedencia (para móvil).
function capMobile(data: GraphResponse): GraphResponse {
  if (data.nodes.length <= MOBILE_MAX) return data;
  const mems = data.nodes.filter((n) => n.type === 'memory').sort((a, b) => (b.occurrences ?? 1) - (a.occurrences ?? 1));
  const adj = new Map<string, string[]>();
  for (const e of data.edges) {
    (adj.get(e.source) ?? adj.set(e.source, []).get(e.source)!).push(e.target);
    (adj.get(e.target) ?? adj.set(e.target, []).get(e.target)!).push(e.source);
  }
  const kept = new Set<string>();
  for (const m of mems) { if (kept.size >= MOBILE_MAX) break; kept.add(m.id); for (const nb of adj.get(m.id) ?? []) if (kept.size < MOBILE_MAX) kept.add(nb); }
  return { meta: data.meta, nodes: data.nodes.filter((n) => kept.has(n.id)), edges: data.edges.filter((e) => kept.has(e.source) && kept.has(e.target)) };
}

// Layout de fuerzas SÍNCRONO (una vez). Devuelve posiciones finales.
function layout(nodes: MemoryNode[], edges: GraphResponse['edges'], W: number, H: number): P[] {
  const pos: P[] = nodes.map((_, i) => ({
    x: W / 2 + Math.cos(i * 2.399) * (30 + (i % 9) * 6), y: H / 2 + Math.sin(i * 2.399) * (30 + (i % 7) * 6),
  }));
  const idx: Record<string, number> = {}; nodes.forEach((n, i) => { idx[n.id] = i; });
  const links = edges.map((e) => ({ s: idx[e.source], t: idx[e.target], sim: e.rel === 'similar_to' }))
    .filter((l) => l.s != null && l.t != null && l.s !== l.t);
  const iters = 130;
  for (let it = 0; it < iters; it++) {
    const k = 1 - it / iters;
    for (let i = 0; i < pos.length; i++)
      for (let j = i + 1; j < pos.length; j++) {
        let dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y;
        const d2 = dx * dx + dy * dy || 0.01, d = Math.sqrt(d2);
        const rep = 2400 / d2, fx = (dx / d) * rep * k, fy = (dy / d) * rep * k;
        pos[i].x += fx; pos[i].y += fy; pos[j].x -= fx; pos[j].y -= fy;
      }
    for (const l of links) {
      let dx = pos[l.t].x - pos[l.s].x, dy = pos[l.t].y - pos[l.s].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const target = l.sim ? 52 : 88, stiff = l.sim ? 0.07 : 0.03;
      const f = (d - target) * stiff * k, fx = (dx / d) * f, fy = (dy / d) * f;
      pos[l.s].x += fx; pos[l.s].y += fy; pos[l.t].x -= fx; pos[l.t].y -= fy;
    }
    for (let i = 0; i < pos.length; i++) { pos[i].x += (W / 2 - pos[i].x) * 0.008 * k; pos[i].y += (H / 2 - pos[i].y) * 0.008 * k; }
  }
  return pos;
}

export function MobileMemoryGraph({ data: raw, onClose, colorBy = 'clasif', correlacion = false, onToggleCorrelacion }: {
  data: GraphResponse; onClose: () => void; colorBy?: 'clasif' | 'cluster'; correlacion?: boolean; onToggleCorrelacion?: () => void;
}) {
  const mode = useThemeMode();
  const S = STAGE[mode];
  const W = window.innerWidth, H = window.innerHeight;
  const data = useMemo(() => capMobile(raw), [raw]);
  const idx = useMemo(() => { const m: Record<string, number> = {}; data.nodes.forEach((n, i) => { m[n.id] = i; }); return m; }, [data]);
  const pos = useMemo(() => layout(data.nodes, data.edges, W, H), [data, W, H]);
  const maxOcc = useMemo(() => Math.max(1, ...data.nodes.map((n) => n.occurrences ?? 1)), [data]);
  const clusters = data.meta.similarity?.clusters;

  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [selected, setSelected] = useState<MemoryNode | null>(null);
  const touch = useRef({ mode: 'none' as 'none' | 'pan' | 'pinch', x: 0, y: 0, dist: 0, moved: 0 });

  function ts(e: React.TouchEvent) {
    if (e.touches.length === 1) { touch.current.mode = 'pan'; touch.current.x = e.touches[0].clientX; touch.current.y = e.touches[0].clientY; touch.current.moved = 0; }
    else if (e.touches.length === 2) { touch.current.mode = 'pinch'; touch.current.dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY); }
  }
  function tm(e: React.TouchEvent) {
    if (touch.current.mode === 'pan' && e.touches.length === 1) {
      const dx = e.touches[0].clientX - touch.current.x, dy = e.touches[0].clientY - touch.current.y;
      touch.current.x = e.touches[0].clientX; touch.current.y = e.touches[0].clientY; touch.current.moved += Math.abs(dx) + Math.abs(dy);
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    } else if (touch.current.mode === 'pinch' && e.touches.length === 2) {
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      const r = d / (touch.current.dist || d); touch.current.dist = d;
      setView((v) => ({ ...v, k: Math.max(0.4, Math.min(4, v.k * r)) }));
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[200] overflow-hidden" style={{ background: S.bg }}>
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', paddingBottom: 12, color: S.text }}>
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><Sparkles size={15} /> Memoria del agente</div>
          <div className="text-[11px]" style={{ color: S.textDim }}>
            {data.nodes.filter((n) => n.type === 'memory').length} memorias{correlacion && clusters != null ? ` · ${clusters} clusters` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onToggleCorrelacion && (
            <button onClick={onToggleCorrelacion} aria-label="Correlación por clusters"
              className={`h-9 px-3 rounded-full flex items-center gap-1.5 text-xs font-medium backdrop-blur-md border active:scale-95 ${
                correlacion ? 'bg-pink-500/20 border-pink-400/40 text-pink-300' : S.glass}`}>
              <Network size={14} /> Clusters
            </button>
          )}
          <button onClick={onClose} aria-label="Cerrar"
            className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md border active:scale-95 ${S.glass}`}>
            <X size={18} />
          </button>
        </div>
      </div>

      <svg width={W} height={H} className="touch-none select-none block" onTouchStart={ts} onTouchMove={tm}
        style={{ animation: 'mmg-in 500ms ease-out' }}>
        <style>{`@keyframes mmg-in{from{opacity:0}to{opacity:1}}`}</style>
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {data.edges.map((e, i) => {
            const a = pos[idx[e.source]], b = pos[idx[e.target]]; if (!a || !b) return null;
            const sim = e.rel === 'similar_to', corr = e.rel === 'corrects';
            return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={sim ? '#ec4899' : corr ? '#f59e0b' : S.edge} strokeWidth={sim || corr ? 2 : 1}
              strokeLinecap="round" opacity={sim || corr ? 0.85 : mode === 'dark' ? 0.4 : 0.55} />;
          })}
          {data.nodes.map((n, i) => {
            const p = pos[i]; if (!p) return null;
            const r = nodeRadius(n, maxOcc);
            const onTap = (ev: React.TouchEvent) => { ev.stopPropagation(); if (touch.current.moved < 8 && n.type === 'memory') setSelected(n); };
            return (
              <g key={n.id} transform={`translate(${p.x} ${p.y})`} onTouchEnd={onTap}>
                <MobileNode node={n} r={r} colorBy={colorBy} mode={mode} />
                {n.type === 'memory' && (
                  <text y={r + 12} textAnchor="middle" fill={S.text} fontSize={10} style={{ pointerEvents: 'none' }}>
                    {trunc(n.label, 18)}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      <div className="absolute z-10 left-4 text-[11px]" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', color: S.textDim }}>
        1 dedo mueve · 2 dedos zoom · toca una memoria
      </div>

      {selected && <BottomSheet node={selected} onClose={() => setSelected(null)} mode={mode} />}
    </div>,
    document.body,
  );
}

function nodeRadius(n: MemoryNode, maxOcc: number): number {
  if (n.type === 'org') return 16;
  if (n.type === 'memory') return 8 + 13 * (Math.log((n.occurrences ?? 1) + 1) / Math.log(maxOcc + 1));
  if (n.type === 'decision') return 6;
  return 8;
}

// Nodo LIGERO — halo por círculo translúcido (sin filtro SVG).
function MobileNode({ node, r, colorBy, mode }: { node: MemoryNode; r: number; colorBy: 'clasif' | 'cluster'; mode: ThemeMode }) {
  const vetoed = node.governance?.status === 'vetoed';
  if (node.type === 'memory') {
    const fill = colorBy === 'cluster' ? cluster(mode, node.cluster_id) : CLASIF[mode][(node.clasif ?? 'publica') as Clasif];
    return (
      <>
        <circle r={r + 5} fill={fill} opacity={mode === 'dark' ? 0.16 : 0.12} />
        <circle r={r} fill={fill} fillOpacity={0.92} stroke={vetoed ? '#ef4444' : mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.85)'} strokeWidth={vetoed ? 2 : 1} strokeDasharray={vetoed ? '3 2' : undefined} />
      </>
    );
  }
  if (node.type === 'document') return <rect x={-r} y={-r * 0.72} width={r * 2} height={r * 1.44} rx={3} fill="#0ea5e9" fillOpacity={0.9} />;
  if (node.type === 'person') return <circle r={r} fill={mode === 'dark' ? '#a78bfa' : '#7c3aed'} fillOpacity={0.9} />;
  if (node.type === 'decision') return <polygon points={`0,${-r} ${r},0 0,${r} ${-r},0`} fill={mode === 'dark' ? '#f5c518' : '#d99e00'} fillOpacity={0.92} />;
  if (node.type === 'org') return <polygon points={hex(r)} fill={mode === 'dark' ? '#334155' : '#64748b'} stroke="#94a3b8" strokeOpacity={0.5} />;
  return <circle r={r} fill="#94a3b8" />;
}

function BottomSheet({ node, onClose, mode }: { node: MemoryNode; onClose: () => void; mode: ThemeMode }) {
  const S = STAGE[mode];
  const gov = node.governance?.status;
  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end" onClick={onClose}>
      <div className="bg-black/40 absolute inset-0" />
      <div className={`relative rounded-t-3xl border-t px-5 pt-3 pb-8 max-h-[70vh] overflow-y-auto ${S.sheet}`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', color: S.sheetText }}
        onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full" style={{ background: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
        <p className="text-sm font-semibold">{node.label || 'Memoria'}</p>
        <dl className="mt-3 space-y-2.5 text-sm">
          <SRow k="Clasificación" mode={mode} v={<span className="capitalize" style={{ color: CLASIF[mode][(node.clasif ?? 'publica') as Clasif] }}>{node.clasif ?? '—'}</span>} />
          <SRow k="Gobernanza" mode={mode} v={<span style={{ color: gov === 'approved' ? '#10b981' : gov === 'vetoed' ? '#ef4444' : S.textDim }}>{gov === 'approved' ? '✓ aprobada' : gov === 'vetoed' ? `⛔ vetada · ${node.governance?.veto_module ?? ''}` : 'sin veredicto'}</span>} />
          <SRow k="Alimentada por" mode={mode} v={node.fed_by || '—'} />
          <SRow k="Origen" mode={mode} v={`${node.source_type ?? '—'}${node.doc_ref ? `: ${node.doc_ref}` : ''}`} />
          <SRow k="Ingresada" mode={mode} v={node.ingested_at || '—'} />
          <SRow k="Reforzada" mode={mode} v={`${node.occurrences ?? 1}×`} />
          <SRow k="Decisión firmada" mode={mode} v={<span className="text-[11px]">🔏 {node.decision_id || '—'}</span>} />
        </dl>
      </div>
    </div>
  );
}

function SRow({ k, v, mode }: { k: string; v: React.ReactNode; mode: ThemeMode }) {
  return <div className="flex items-start justify-between gap-3"><dt className="text-xs shrink-0 pt-0.5" style={{ color: STAGE[mode].textDim }}>{k}</dt><dd className="text-right">{v}</dd></div>;
}
function hex(r: number): string { return Array.from({ length: 6 }, (_, i) => { const a = (Math.PI / 3) * i - Math.PI / 2; return `${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`; }).join(' '); }
function trunc(s: string, n: number): string { return s && s.length > n ? s.slice(0, n) + '…' : s || ''; }
